import { useState } from 'react'
import { useFormik } from 'formik'
import { FolderDocument, EquipmentIcon, InputDateTime, Button } from '~/components'
import moment from 'moment'
import { Colors } from '~/styles'
import {
  GetDurationMinutes,
  GetDurationDays,
  getDurationBetweenTwoTime,
  durationToFormattedString
} from '~/utils'

import SC from './styled.activity'
import Funcs from './functions.activity'
import { ConvertLocalTimeGet } from '../../../utils'
import { Images } from '../../../assets'
import Popover from './popover'
import { FormControl, MenuItem, Select } from '@material-ui/core'

const ActivityNameCell = ({ activity, handleOpenActivity, isActivityChecklist }) => {
  const handleChange = () => {
    handleOpenActivity(activity)
  }

  const getColor = () => {
    if (activity.progress_task_header_list.some(detail => detail.is_red)) {
      return '#E82D2D' // Red
    }
    if (activity.progress_task_header_list.some(detail => detail.is_orange)) {
      return '#FFB000' // Orange
    }
    if (activity.progress_task_header_list.every(detail => detail.is_green)) {
      return '#95C123' // Green
    }
    return '#777777' // Default color
  }

  const color = getColor()

  return (
    <SC.TableData>
      <SC.ActivityCellWrapper>
        <SC.KeyboardArrowDownIcon
          isOpen={activity.isOpen}
          onClick={handleChange}
        />
        {isActivityChecklist && (
          <SC.ColorStatus size='small' style={{ backgroundColor: color }} />
        )}
        <span>{activity.activity_name}</span>
      </SC.ActivityCellWrapper>
    </SC.TableData>
  )
}

const AddTaskRow = ({ activity, addNewTask }) => {
  const [taskDescription, setTaskDescription] = useState('')

  const handleChangeTaskDescription = (event) => {
    setTaskDescription(event.target.value)
  }

  const handleAddNewTask = () => {
    // false is mean activity type !== Checklist
    addNewTask(activity, taskDescription, false)
    setTaskDescription('')
  }

  return (
    <tr>
      <SC.TableData colSpan={9}>
        <SC.AddTaskCell>
          <SC.AddTaskInput
            placeholder='Add New Task'
            value={taskDescription}
            onChange={handleChangeTaskDescription}
          />
          <SC.AddTaskButtonWrapper>
            <Button
              disable={taskDescription === '' ? true : false}
              type='submit'
              color={taskDescription === '' ? 'grey' : Colors.newGreen}
              onClick={handleAddNewTask}
            >
              Add Task
            </Button>
          </SC.AddTaskButtonWrapper>
        </SC.AddTaskCell>
      </SC.TableData>
    </tr>
  )
}

const TaskNameCell = ({ activity, index, task, handleOpenTask, isActivityChecklist, getColor }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedTask, setSelectedTask] = useState()
  const open = Boolean(anchorEl)

  const handleChange = () => {
    handleOpenTask(activity, task)
  }

  const handlePopoverOpen = (task) => (event) => {
    setAnchorEl(event.currentTarget)
    setSelectedTask(task)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const showPopoverIcon = task.pic || task.level_value || task.requirement || task.compliance_parameter

  return (
    <SC.TaskCellWrapper isActivityChecklist={isActivityChecklist}>
      {task.progress_task_detail_list && (
        <SC.KeyboardArrowDownIcon isOpen={task.isOpen} onClick={handleChange} />
      )}
      {isActivityChecklist && (
        <SC.ColorStatus size='small' style={{ backgroundColor: getColor(task) }} />
      )}
      {isActivityChecklist ? (
        <SC.FirstChildCellArranger>
          <span>{task.task}</span>
          {showPopoverIcon && (
            <SC.IconWrapper>
              <SC.ExclamationIcon src={Images.ExclamationIcon} onClick={handlePopoverOpen(task)} />
            </SC.IconWrapper>
          )}
          <Popover
            open={open}
            task={selectedTask}
            anchorEl={anchorEl}
            handlePopoverClose={handlePopoverClose}
          />
        </SC.FirstChildCellArranger>
      ) : (
        <span>{`${index}. ${task.task}`}</span>
      )}
    </SC.TaskCellWrapper>
  )
}

const DetailGridHeader = () => (
  <thead>
    <SC.InnerGridHeader>
      <SC.TableData width='auto'>Equipment/Labour</SC.TableData>
      <SC.TableData width='300px'>Unit/Name</SC.TableData>
      <SC.TableData width='100px'>Qty</SC.TableData>
      <SC.TableData width='200px'>Actual Start</SC.TableData>
      <SC.TableData width='200px'>Actual Finish</SC.TableData>
      <SC.TableData width='100px'>Breaktime</SC.TableData>
      <SC.TableData width='200px'>Duration</SC.TableData>
      <SC.TableData width='200px'>Billing Start</SC.TableData>
      <SC.TableData width='200px'>Billing Finish</SC.TableData>
      <SC.TableData width='200px'>Billing Duration</SC.TableData>
    </SC.InnerGridHeader>
  </thead>
)

const EquipmentTaskRow = ({ taskDetail, location }) => {
  return (
    <tr>
      <SC.TableData>{taskDetail.description}</SC.TableData>
      <SC.TableData>
        {taskDetail.unit_name_code} - {taskDetail.unit_name_description}
      </SC.TableData>
      <SC.TableData>{taskDetail.qty}</SC.TableData>
      <SC.TableData>
        {taskDetail.start_time
          ? ConvertLocalTimeGet(
            location,
            taskDetail.start_time,
            'YYYY-MM-DD HH:mm'
          )
          : null}
      </SC.TableData>
      <SC.TableData>
        {taskDetail.finish_time
          ? ConvertLocalTimeGet(
            location,
            taskDetail.finish_time,
            'YYYY-MM-DD HH:mm'
          )
          : null}
      </SC.TableData>
      <SC.TableData>{taskDetail.break_time}</SC.TableData>
      <SC.TableData>
        {GetDurationMinutes(
          taskDetail.start_time,
          taskDetail.finish_time,
          taskDetail.break_time
        )}
      </SC.TableData>

      <SC.TableData>
        {taskDetail.billing_start_time
          ? ConvertLocalTimeGet(
            location,
            taskDetail.billing_start_time,
            'YYYY-MM-DD HH:mm'
          )
          : null}
      </SC.TableData>
      <SC.TableData>
        {taskDetail.billing_finish_time
          ? ConvertLocalTimeGet(
            location,
            taskDetail.billing_finish_time,
            'YYYY-MM-DD HH:mm'
          )
          : null}
      </SC.TableData>
      <SC.TableData>
        {GetDurationMinutes(
          taskDetail.billing_start_time,
          taskDetail.billing_finish_time,
          taskDetail.break_time
        )}
      </SC.TableData>
    </tr>
  )
}

const EquipmentTaskEmpty = () => {
  return (
    <tbody>
      <tr>
        <SC.TableData colSpan='6'>
          <SC.EmptyListCell>No Equipment Tasks Available</SC.EmptyListCell>
        </SC.TableData>
      </tr>
    </tbody>
  )
}

const EquipmentTaskGridBody = ({ location, taskDetailList }) => (
  <tbody>
    {taskDetailList.map((taskDetail) => (
      <EquipmentTaskRow
        location={location}
        key={taskDetail.id}
        taskDetail={taskDetail}
      />
    ))}
  </tbody>
)

const DetailGridBody = ({ location, taskDetailList }) => {
  return (taskDetailList ?? []).length === 0 ? (
    <EquipmentTaskEmpty />
  ) : (
    <EquipmentTaskGridBody
      location={location}
      taskDetailList={taskDetailList}
    />
  )
}

const DetailGrid = ({ location, taskDetailList, handleShowModal, activity, setActivityList, isActivityChecklist }) => (
  <SC.BodyRowGridDetail>
    <SC.TableData colSpan={isActivityChecklist ? 12 : 9}>
      <SC.InnerGrid>
        <DetailGridHeader
          activity={activity}
          setActivityList={setActivityList}
        />
        <DetailGridBody
          location={location}
          taskDetailList={taskDetailList}
          handleShowModal={handleShowModal}
        />
      </SC.InnerGrid>
    </SC.TableData>
  </SC.BodyRowGridDetail>
)

const HeaderGrid = () => (
  <SC.THead>
    <SC.HeaderRowGrid>
      <SC.TableHead width='250px'>List Activity</SC.TableHead>
      <SC.TableHead width='200px'>Actual Start</SC.TableHead>
      <SC.TableHead width='200px'>Actual Finish</SC.TableHead>
      <SC.TableHead width='160px'>Actual Duration in Hours</SC.TableHead>
      <SC.TableHead width='auto'>Actual Duration in Days</SC.TableHead>
      <SC.TableHead width='200px'>Billing Start</SC.TableHead>
      <SC.TableHead width='200px'>Billing Finish</SC.TableHead>
      <SC.TableHead width='160px'>Billing Duration in Hours</SC.TableHead>
      <SC.TableHead width='auto'>Billing Duration in Days</SC.TableHead>
      <SC.TableHead width='50px'></SC.TableHead>
      <SC.TableHead width='50px'></SC.TableHead>
    </SC.HeaderRowGrid>
  </SC.THead>
)

const TaskRow = ({
  task,
  index,
  activity,
  location,
  getColor,
  deleteTask,
  inEditMode,
  handleOpenTask,
  handleShowModal,
  handleShowModalDoc,
  isActivityChecklist,
  updateActivityAndTaskHeader
}) => {
  const form = useFormik({
    initialValues: {
      sr_code: task.sr_code,
      code_activity: activity.code,
      actual_start_time: task.actual_start_time ?? '',
      actual_finish_time: task.actual_finish_time ?? '',
      actual_dur_time: task.actual_dur_time ?? 0,
      billng_start_time: task.billng_start_time ?? '',
      billng_finish_time: task.billng_finish_time ?? '',
      billng_dur_time: task.billng_dur_time ?? 0
    },
    enableReinitialize: true,
    validateOnChange: true,
    validate: (values) => {
      const billng_start_time =
        values.actual_start_time == task.actual_start_time
          ? values.billng_start_time
          : values.actual_start_time
      const billng_finish_time =
        values.actual_finish_time == task.actual_finish_time
          ? values.billng_finish_time
          : values.actual_finish_time
      const billng_dur_time =
        values.actual_dur_time == task.actual_dur_time
          ? values.billng_dur_time
          : values.actual_dur_time
      updateActivityAndTaskHeader(activity, {
        ...task,
        ...values,
        actual_start_time: values.actual_start_time,
        actual_finish_time: values.actual_finish_time,
        billng_start_time: billng_start_time,
        billng_finish_time: billng_finish_time,
        billng_dur_time: billng_dur_time
      })
    }
  })

  const disabled = !form.values.billng_start_time || !form.values.billng_finish_time

  return (
    <>
      <SC.BodyRowGrid key={task.id}>
        {isActivityChecklist && (
          <SC.TableData disableBorder={isActivityChecklist}>
            {index}
          </SC.TableData>
        )}
        <TaskNameCell
          task={task}
          index={index}
          getColor={getColor}
          activity={activity}
          handleOpenTask={handleOpenTask}
          isActivityChecklist={isActivityChecklist}
        />
        <SC.TableData disableBorder={isActivityChecklist}>
          <InputDateTime
            tabIndex={3}
            readOnly={!inEditMode}
            name='actual_start_time'
            form={form}
            id='exe-mdl-act-start-time-date'
          />
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          <InputDateTime
            tabIndex={3}
            readOnly={!inEditMode}
            name='actual_finish_time'
            form={form}
            id='exe-mdl-act-start-time-date'
          />
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          {Funcs.durationToFormattedStringHours(form.values.actual_dur_time)}
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          {Funcs.durationtoFormattedStringDays(form.values.actual_dur_time)}
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          <InputDateTime
            form={form}
            tabIndex={3}
            readOnly={!inEditMode}
            name='billng_start_time'
            id='exe-mdl-act-start-time-date'
          />
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          <InputDateTime
            form={form}
            tabIndex={3}
            readOnly={!inEditMode}
            name='billng_finish_time'
            id='exe-mdl-act-start-time-date'
          />
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          {Funcs.durationToFormattedStringHours(form.values.billng_dur_time)}
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          {Funcs.durationtoFormattedStringDays(form.values.billng_dur_time)}
        </SC.TableData>
        <SC.TableData disableBorder={isActivityChecklist}>
          <SC.EquipmentIconWrapper
            disabled={disabled}
            onClick={() => !disabled && handleShowModal(activity.code, task.code, task.task)}
          >
            <EquipmentIcon width='20px' height='20px' color={disabled ? Colors.hintGrey : Colors.softBlue} />
            {isActivityChecklist && `(${(task.progress_task_detail_list || []).length})`}
          </SC.EquipmentIconWrapper>
        </SC.TableData>
        {isActivityChecklist && (
          <SC.TableData disableBorder={isActivityChecklist}>
            <SC.EquipmentIconWrapper
              onClick={() => handleShowModalDoc(task, activity.code, task.code)}
            >
              <FolderDocument width='20px' height='20px' color={Colors.newGreen} />
              {`(${(task.progress_task_attachment_list || []).length})`}
            </SC.EquipmentIconWrapper>
          </SC.TableData>
        )}
        <SC.TableData disableBorder={isActivityChecklist}>
          <SC.EquipmentIconWrapper
            onClick={inEditMode && (() => deleteTask(activity, task))}
          >
            <SC.DeleteIcon disable={!inEditMode} />
          </SC.EquipmentIconWrapper>
        </SC.TableData>
      </SC.BodyRowGrid>
      {task.isOpen && task.progress_task_detail_list ? (
        <DetailGrid
          location={location}
          isActivityChecklist={isActivityChecklist}
          taskDetailList={task.progress_task_detail_list ?? []}
        />
      ) : null}
    </>
  )
}
const SortTask = ({ activityData, setActivityList, location, modal, index, setModal }) => {
  const [dataSort, setDataSort] = useState([])
  return <div
    style={{ width: '100%', }}
  >
    <SC.SortWrapper
      // modal===index && sort-icon is clicked then close the <modal/> else open modal based on index
      onClick={() => setModal(
        modal === index ?
          -1 : index
      )}>
      <img src={Images.SortIcon} />
      sort
    </SC.SortWrapper>

    {modal === index &&
      (
        // modal===index as flagging modal showed or not
        <SortModal
          dataSort={dataSort}
          location={location}
          activityData={activityData}
          setActivityList={setActivityList}
          setModalChild={(e) => setModal(e)}
          setDataSortChild={(e) => setDataSort(e)}
        />
      )}
  </div>
}


const SortModal = ({ setDataSortChild, dataSort, setModalChild, activityData, setActivityList, location }) => {
  const dataDropdown = [
    { field: 'task', name: 'Task', id: 1, dir: 'asc' },
    { field: 'actual_start_time', name: 'Actual Start', id: 2, dir: 'asc' },
    { field: 'actual_finish_time', name: 'Actual Finish', id: 3, dir: 'asc' },
    { field: 'billng_start_time', name: 'Billing Start', id: 4, dir: 'asc' },
    { field: 'billng_finish_time', name: 'Billing Finish', id: 5, dir: 'asc' },
  ]

  const dataFilterModule = () => {
    var dataSlice = dataDropdown
    dataSort.forEach((datal) => {
      const index = dataDropdown.findIndex((item) => item.id == datal.id)
      dataSlice.splice(index, 1)
    })
    return dataSlice
  }


  const handleAdd = (event) => {
    const arr = [...dataSort]
    arr.push(event.target.value)
    setDataSortChild(arr)
  }

  const handleRemove = (event) => {
    const arr = dataSort.filter((item) => {
      return item.id != event.id
    })
    setDataSortChild(arr)
  }
  const handleSort = (event) => {
    const arr = dataSort.map((item) => {
      if (item.id == event.id) {
        if (item.dir == 'asc') {
          return { ...item, dir: 'desc' }
        } else {
          return { ...item, dir: 'asc' }
        }
      } else {
        return item
      }
    })
    setDataSortChild(arr)
  }

  const handleClose = () => {
    setModalChild(-1)
  }

  const handleUpdate = async () => {
    const payload = {
      dataSort: dataSort,
      data: {
        activity_code: activityData.code,
        sr_code: activityData.sr_code,
        modified_date: new Date()
      },
      handleClose: () => setModalChild(-1),
      location: location,
      setActivityList: setActivityList
    }
    await Funcs.updateSort(payload)
  }
  return (
    <div style={{ position: 'relative' }}>
      <SC.ModalSort>
        <div style={{ borderBottom: '1px solid black', width: '100%' }}>
          {dataSort.map((item, i) => (
            <SC.SortListWrapper key={i}>
              <SC.SortTextList>{item.name}</SC.SortTextList>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div>
                  <img src={Images.sortDirIcon}
                    style={{
                      height: '100%', width: '100%',
                      transform: `rotate(${item.dir == 'asc' ? '0deg' : '180deg'})`,
                      marginRight: 5, cursor: 'pointer', color: '#A3AAB6'
                    }}
                    onClick={() => handleSort(item)}
                  />
                </div>
                <div>
                  <img src={Images.deleteIcon}
                    style={{ height: '100%', width: '100%', cursor: 'pointer', color: '#A3AAB6' }}
                    onClick={() => handleRemove(item)}
                  />
                </div>
              </div>
            </SC.SortListWrapper>
          ))}
          <FormControl
            style={{ display: 'flex', justifyContent: 'center', margin: '10px 15px 0px' }}
            sx={{
              boxShadow: 'none',
              '.MuiOutlinedInput-notchedOutline': { border: 0 }
            }}
            size='small'
          >
            <Select
              disableUnderline
              displayEmpty={true}
              renderValue={() => '  + Add sort'}
              onChange={(e) => handleAdd(e)}
              style={{
                marginBottom: 5,
                backgroundColor: 'transparent !important'
              }}
            >
              {dataFilterModule().map((item, i) => (
                <MenuItem key={i} value={item}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div
          style={{
            margin: '10px 0 5px 0',
            direction: 'rtl',
            width: '100%',
            display: 'flex'
          }}
        >
          <SC.ButtonFilter onClick={handleUpdate}>Update</SC.ButtonFilter>
          <SC.ButtonClose onClick={handleClose}>Close</SC.ButtonClose>
        </div>
      </SC.ModalSort>
    </div>
  )
}

const TaskGridHeader = () => (
  <thead>
    <SC.TaskGridHeader>
      <SC.TableData disableBorder width='50px'>No.</SC.TableData>
      <SC.TableData disableBorder width='250px'>List Activity</SC.TableData>
      <SC.TableData disableBorder width='200px'>Actual Start</SC.TableData>
      <SC.TableData disableBorder width='200px'>Actual Finish</SC.TableData>
      <SC.TableData disableBorder width='160px'>Actual Duration in Hours</SC.TableData>
      <SC.TableData disableBorder width='auto'>Actual Duration in Days</SC.TableData>
      <SC.TableData disableBorder width='200px'>Billing Start</SC.TableData>
      <SC.TableData disableBorder width='200px'>Billing Finish</SC.TableData>
      <SC.TableData disableBorder width='160px'>Billing Duration in Hours</SC.TableData>
      <SC.TableData disableBorder width='auto'>Billing Duration in Days</SC.TableData>
      <SC.TableData disableBorder width='50px'></SC.TableData>
    </SC.TaskGridHeader>
  </thead>
)

const TaskList = ({
  tasks,
  activity,
  getColor,
  location,
  deleteTask,
  inEditMode,
  handleOpenTask,
  handleShowModal,
  handleShowModalDoc,
  isActivityChecklist,
  updateActivityAndTaskHeader
}) => {
  if (isActivityChecklist) {
    return (
      <SC.BodyRowGridTask>
        <SC.TableData style={{ paddingTop: 0 }} disableBorder colSpan={11}>
          <TaskGridHeader />
          {tasks.map((task, index) => (
            <TaskRow
              task={task}
              key={task.id}
              index={index + 1}
              getColor={getColor}
              location={location}
              activity={activity}
              isActivityChecklist
              inEditMode={inEditMode}
              deleteTask={deleteTask}
              handleOpenTask={handleOpenTask}
              handleShowModal={handleShowModal}
              handleShowModalDoc={handleShowModalDoc}
              updateActivityAndTaskHeader={updateActivityAndTaskHeader}
            />
          ))}
        </SC.TableData>
      </SC.BodyRowGridTask>
    )
  }
  return tasks.map((task, index) => (
    <TaskRow
      task={task}
      key={task.id}
      index={index + 1}
      location={location}
      activity={activity}
      isActivityChecklist={false}
      inEditMode={inEditMode}
      deleteTask={deleteTask}
      handleOpenTask={handleOpenTask}
      handleShowModal={handleShowModal}
      handleShowModalDoc={handleShowModalDoc}
      updateActivityAndTaskHeader={updateActivityAndTaskHeader}
    />
  ))
}

const EmptyList = ({ text }) => (
  <tr>
    <SC.TableData colSpan={9}>
      <SC.EmptyListCell>{text}</SC.EmptyListCell>
    </SC.TableData>
  </tr>
)

const ActivityList = ({
  data,
  location,
  getColor,
  addNewTask,
  deleteTask,
  inEditMode,
  isFinalized,
  setActivityList,
  handleOpenTask,
  handleShowModal,
  handleOpenActivity,
  handleShowModalDoc,
  handleShowModalTask,
  isActivityChecklist,
  updateActivityAndTaskHeader
}) => {
  const [modal, setModal] = useState(-1)
  return (data || []).map((activity, index) => {
    const actualDur = getDurationBetweenTwoTime(
      activity.start_date,
      activity.finish_date
    )
    const actualDurFormatted = durationToFormattedString(actualDur)

    return (
      <>
        <SC.BodyRowGrid showNextRow={activity.isOpen} key={activity.id}>
          <ActivityNameCell
            activity={activity}
            handleOpenActivity={handleOpenActivity}
            isActivityChecklist={isActivityChecklist}
          />
          <SC.TableData>
            {activity.start_date
              ? moment(activity.start_date).format('DD-MM-YYYY HH:mm')
              : null}
          </SC.TableData>
          <SC.TableData>
            {activity.finish_date
              ? moment(activity.finish_date).format('DD-MM-YYYY HH:mm')
              : null}
          </SC.TableData>
          <SC.TableData>{actualDurFormatted}</SC.TableData>
          <SC.TableData>
            {GetDurationDays(activity.start_date, activity.finish_date, 0)}
          </SC.TableData>
          <SC.TableData></SC.TableData>
          <SC.TableData></SC.TableData>
          <SC.TableData></SC.TableData>
          <SC.TableData colSpan={6} style={{ paddingLeft: '9vw' }} >
            {inEditMode &&
              <SortTask
                inEditMode={inEditMode}
                activityData={activity}
                modal={modal}
                index={index}
                setModal={setModal}
                location={location}
                setActivityList={setActivityList} />}
          </SC.TableData>
        </SC.BodyRowGrid>
        {activity.isOpen && isActivityChecklist && (
          <SC.BodyRowGridTask>
            <SC.TableDataTask style={{ paddingBottom: 0 }} colSpan={9}>Task</SC.TableDataTask>
            <SC.TableDataTask style={{ paddingBottom: 0 }} colSpan={2}>
              {!isFinalized && inEditMode && (
                <SC.AddTaskButton onClick={() => handleShowModalTask(activity)}>
                  <img src={Images.AddTask} alt='Add Task' />
                  Add Task
                </SC.AddTaskButton>
              )}
            </SC.TableDataTask>
          </SC.BodyRowGridTask>
        )}
        {activity.isOpen && (
          <TaskList
            getColor={getColor}
            activity={activity}
            location={location}
            inEditMode={inEditMode}
            deleteTask={deleteTask}
            handleOpenTask={handleOpenTask}
            handleShowModal={handleShowModal}
            handleShowModalDoc={handleShowModalDoc}
            isActivityChecklist={isActivityChecklist}
            tasks={activity.progress_task_header_list ?? []}
            updateActivityAndTaskHeader={updateActivityAndTaskHeader}
          />
        )}
        {inEditMode && !isActivityChecklist && (
          <AddTaskRow activity={activity} addNewTask={addNewTask} />
        )}
      </>
    )
  })
}

const BodyGrid = ({
  data,
  getColor,
  location,
  addNewTask,
  deleteTask,
  inEditMode,
  isFinalized,
  handleOpenTask,
  setActivityList,
  handleShowModal,
  handleOpenActivity,
  handleShowModalDoc,
  isActivityChecklist,
  handleShowModalTask,
  updateActivityAndTaskHeader
}) => (
  <tbody>
    {data.length === 0 ? (
      <EmptyList text='No Activities' />
    ) : (
      <ActivityList
        data={data}
        getColor={getColor}
        location={location}
        inEditMode={inEditMode}
        addNewTask={addNewTask}
        deleteTask={deleteTask}
        isFinalized={isFinalized}
        setActivityList={setActivityList}
        handleOpenTask={handleOpenTask}
        handleShowModal={handleShowModal}
        handleShowModalDoc={handleShowModalDoc}
        handleOpenActivity={handleOpenActivity}
        handleShowModalTask={handleShowModalTask}
        isActivityChecklist={isActivityChecklist}
        updateActivityAndTaskHeader={updateActivityAndTaskHeader}
      />
    )}
  </tbody>
)

const ActivityGrid = ({
  data,
  location,
  getColor,
  inEditMode,
  addNewTask,
  deleteTask,
  isFinalized,
  handleOpenTask,
  handleShowModal,
  setActivityList,
  handleOpenActivity,
  handleShowModalDoc,
  handleShowModalTask,
  isActivityChecklist,
  updateActivityAndTaskHeader
}) => (
  <SC.GridContainer>
    <SC.Grid>
      <HeaderGrid />
      <BodyGrid
        data={data}
        getColor={getColor}
        location={location}
        inEditMode={inEditMode}
        addNewTask={addNewTask}
        deleteTask={deleteTask}
        isFinalized={isFinalized}
        handleOpenTask={handleOpenTask}
        setActivityList={setActivityList}
        handleShowModal={handleShowModal}
        handleShowModalDoc={handleShowModalDoc}
        handleOpenActivity={handleOpenActivity}
        handleShowModalTask={handleShowModalTask}
        isActivityChecklist={isActivityChecklist}
        updateActivityAndTaskHeader={updateActivityAndTaskHeader}
      />
    </SC.Grid>
  </SC.GridContainer>
)

export default {
  ActivityGrid
}
